import React, { useState, useEffect } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { Plug, ShoppingBag, MessageSquare, CreditCard, ExternalLink, CheckCircle, Circle } from "lucide-react";
import { getOperatorIntegrations } from "../../../../api/settings";
import { getMeliOperatorAuthURL } from "../../../../api/integrations";
import ChannelIntegrations from "../../helpdesk/ChannelIntegrations";
import PaymentIntegrationsSection from "../sections/PaymentIntegrationsSection";
import { showToast } from "../../../../utils/showToast";
import { Button } from "../../../../components/ui/Button";
import { useTranslation } from "react-i18next";

// ─────────────────────────────────────────────────────────────────────────────
// Platform definitions
// ─────────────────────────────────────────────────────────────────────────────

// El operador maneja sus integraciones e-commerce vía LogisticCompany.integrations[]
// (integrationName "MercadoLibre"). Por ahora solo se ofrece MercadoLibre.
const PLATFORMS = [
  {
    key: "mercadolibre",
    label: "MercadoLibre",
    description: "Gestiona envíos de tus publicaciones en MercadoLibre Flex.",
    icon: "🟡",
    docsUrl: "https://docs.beartrackapp.com/integrations/mercadolibre",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Sub-tabs
// ─────────────────────────────────────────────────────────────────────────────

const SUB_TABS = [
  { id: "ecommerce", label: "E-commerce y APIs", icon: ShoppingBag },
  { id: "channels", label: "Canales de comunicación", icon: MessageSquare },
  { id: "payments", label: "Pagos", icon: CreditCard },
];

// ─────────────────────────────────────────────────────────────────────────────
// Platform card
// ─────────────────────────────────────────────────────────────────────────────

function PlatformCard({ platform, active, connecting, onConnect }) {
  const { t } = useTranslation();
  return (
    <article className={`it-card${active ? " it-card--connected" : ""}`}>
      <div className="it-card__logo" aria-hidden="true">{platform.icon}</div>
      <div className="it-card__info">
        <div className="it-card__name-row">
          <h3 className="it-card__name">{platform.label}</h3>
          {active ? (
            <span className="as-badge as-badge--success">
              <CheckCircle size={11} />
              {t("Conectado")}
            </span>
          ) : (
            <span className="as-badge as-badge--neutral">
              <Circle size={11} />
              {t("Desconectado")}
            </span>
          )}
        </div>
        <p className="it-card__desc">{t(platform.description)}</p>
      </div>
      <div className="it-card__actions">
        {platform.docsUrl && (
          <a
            href={platform.docsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 h-8 px-3 text-sm rounded-md font-medium border border-border bg-transparent text-fg no-underline hover:bg-bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
            aria-label={t("Documentación de {{label}}", { label: platform.label })}
          >
            <ExternalLink size={14} />
            Docs
          </a>
        )}
        <Button
          variant={active ? "outline" : "primary"}
          size="sm"
          onClick={() => onConnect(platform)}
          loading={connecting}
          iconLeft={<Plug size={14} />}
        >
          {active ? t("Reconectar") : t("Conectar")}
        </Button>
      </div>
    </article>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// E-commerce sub-section
// ─────────────────────────────────────────────────────────────────────────────

function EcommerceSection() {
  const { t } = useTranslation();
  const location = useLocation();
  // El callback del OAuth deja acá el motivo real del fallo (state del router,
  // nunca la query). Se captura en el render para poder leerlo dentro del
  // efecto de retorno, que corre una sola vez al montar (deps []).
  const meliErrorState = location.state?.meliError;
  const [activeNames, setActiveNames] = useState(new Set());
  const [loading, setLoading] = useState(true);
  // Plataforma cuyo kickoff OAuth está en curso (spinner + guardia doble click).
  const [connecting, setConnecting] = useState(null);

  useEffect(() => {
    // El operador se resuelve desde la sesión en el backend (no se pasa id).
    getOperatorIntegrations()
      .then((res) => {
        const list = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
            ? res
            : [];
        setActiveNames(
          new Set(list.map((i) => String(i.integrationName || "").toLowerCase())),
        );
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Retorno del OAuth del operador: el callback en /admin redirige aquí con
  // ?meli=ok|error. Mostramos el resultado y limpiamos el parámetro (sin perder
  // ?tab=integrations) para que no reaparezca al refrescar.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const meli = params.get("meli");
    if (!meli) return;
    if (meli === "ok") showToast(t("Cuenta de MercadoLibre conectada"), true);
    else if (meli === "error") {
      // Si el callback pudo determinar el motivo, se muestra ESE texto; si no,
      // el genérico.
      const detail =
        typeof meliErrorState === "string" && meliErrorState.trim()
          ? meliErrorState.trim()
          : null;
      showToast(detail || t("No se pudo conectar MercadoLibre"), false);
    }
    params.delete("meli");
    const qs = params.toString();
    // replaceState reescribe la entrada de historial completa, así que también
    // descarta el state del router con el que llegó meliError: al refrescar no
    // reaparece el aviso de un fallo ya mostrado.
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${qs ? `?${qs}` : ""}`,
    );
  }, []);

  // Kickoff OAuth. MercadoLibre: pide la URL de autorización (state=operator) y
  // navega el navegador hacia ella. Otras plataformas aún no están disponibles.
  const handleConnect = async (platform) => {
    if (platform.key !== "mercadolibre") {
      showToast(t("Flujo de {{label}} próximamente", { label: platform.label }), "warning");
      return;
    }
    if (connecting) return; // evita doble kickoff
    setConnecting(platform.key);
    try {
      const res = await getMeliOperatorAuthURL();
      if (res && res.authURL) {
        window.location.assign(res.authURL);
        // No liberamos `connecting`: el botón queda en loading hasta el unload.
      } else {
        showToast(t("No se pudo iniciar la conexión con MercadoLibre"), false);
        setConnecting(null);
      }
    } catch (err) {
      console.error("Error iniciando OAuth de MercadoLibre:", err);
      showToast(t("Ocurrió un error al conectar con MercadoLibre"), false);
      setConnecting(null);
    }
  };

  if (loading) {
    return (
      <div className="it-grid">
        {PLATFORMS.map((p) => (
          <div key={p.key} className="as-skeleton" style={{ height: 130, borderRadius: 12 }} />
        ))}
      </div>
    );
  }

  return (
    <div className="it-grid">
      {PLATFORMS.map((p) => (
        <PlatformCard
          key={p.key}
          platform={p}
          active={activeNames.has(p.key)}
          connecting={connecting === p.key}
          onConnect={handleConnect}
        />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// IntegrationsTab
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_SUB_TAB = "ecommerce";
const isSubTab = (id) => SUB_TABS.some((tab) => tab.id === id);

export default function IntegrationsTab({ company }) {
  const { t } = useTranslation();
  // El sub-tab vive en la URL (?sub=channels): los enlaces que apuntan a una
  // sección puntual —la mesa de ayuda manda acá a vincular canales— tienen que
  // abrir ESA sección, no la primera. Con `replace` al cambiar: moverse entre
  // sub-tabs no es navegación y "atrás" no debería recorrerlas una por una.
  const [searchParams, setSearchParams] = useSearchParams();
  const subParam = searchParams.get("sub");
  const activeSubTab = isSubTab(subParam) ? subParam : DEFAULT_SUB_TAB;

  const handleSubTab = (id) => {
    // Se conservan los otros parámetros (?tab=integrations, ?meli=), pero
    // leyendo la URL VIVA y no el `searchParams` del router: ese es el snapshot
    // del último render y EcommerceSection limpia `?meli=` con
    // window.history.replaceState (react-router no se entera), así que
    // reescribirlo desde el snapshot RESUCITARÍA un `?meli=ok` ya consumido y el
    // aviso de "Cuenta conectada" volvería a salir en cada vuelta al sub-tab.
    // Misma razón por la que useUrlState construye sus writes desde
    // window.location.search.
    const next = new URLSearchParams(window.location.search);
    next.set("sub", id);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="w-full min-w-0 it-root">
      {/* Sub-tab bar */}
      <div className="it-subtabs" role="tablist" aria-label={t("Tipo de integración")}>
        {SUB_TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = tab.id === activeSubTab;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`it-subtab${isActive ? " it-subtab--active" : ""}`}
              onClick={() => handleSubTab(tab.id)}
            >
              <Icon size={16} aria-hidden="true" />
              {t(tab.label)}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div key={activeSubTab} className="as-panel__content--animated">
        {activeSubTab === "ecommerce" && <EcommerceSection />}
        {activeSubTab === "channels" && <ChannelIntegrations />}
        {activeSubTab === "payments" && <PaymentIntegrationsSection company={company} />}
      </div>
    </div>
  );
}
